/**
 * "Retrieve your account" outreach email.
 *
 * Legacy hosts imported from the old SQL/WordPress database keep their listing
 * but have never signed in to the new platform (`wpUserId` set,
 * `accountRetrieved` false). This builds the mailto: draft that chases them,
 * with a hard one-month deadline after which the account and its listings are
 * removed.
 *
 * NOTE on the sender: `mailto:` cannot set the From address — that is chosen by
 * whichever account the admin's mail client is signed in as. Send from the
 * hello@kodefoundry.com mailbox (the address is also signed into the body) or
 * the reply will land somewhere else. A true server-sent version would need a
 * Cloud Function with the transactional mail provider.
 */

import type { User } from './hooks';

export const SUPPORT_EMAIL = 'hello@kodefoundry.com';
export const RETRIEVE_URL = 'https://darak.online/login';

/** Days the host gets before the account and listings are removed. */
export const RETRIEVE_DEADLINE_DAYS = 30;

/**
 * Only legacy hosts who never claimed their account get chased. Guests have
 * nothing to lose by lapsing, and already-retrieved accounts are live.
 */
export const canSendRetrieveEmail = (user: User): boolean =>
  !!user.wpUserId && !user.accountRetrieved && user.role === 'host' && !!user.email;

const formatDeadline = (from: Date): string => {
  const d = new Date(from);
  d.setDate(d.getDate() + RETRIEVE_DEADLINE_DAYS);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

export const buildRetrieveAccountSubject = (): string =>
  'Action required: retrieve your DARAK host account';

export const buildRetrieveAccountBody = (user: User, now: Date = new Date()): string => {
  const name = user.displayName?.trim() || 'there';
  const deadline = formatDeadline(now);

  return [
    `Hi ${name},`,
    '',
    'Your property is listed on DARAK, but your host account has not been retrieved since we moved to the new platform. Until you retrieve it, you cannot manage your listing, respond to guests, or receive bookings.',
    '',
    'How to retrieve your account:',
    '',
    `1. Go to ${RETRIEVE_URL}`,
    `2. Enter this email address: ${user.email}`,
    '3. Follow the prompt to set a new password',
    '4. Sign in and review your listing details',
    '',
    'It takes under two minutes and your existing listing, photos and reviews stay exactly as they are.',
    '',
    `Please note: if the account is not retrieved by ${deadline}, the account and its listings will be permanently deleted from DARAK. This cannot be undone.`,
    '',
    'If you need help, reply to this email and we will walk you through it.',
    '',
    'Best regards,',
    'The DARAK Team',
    SUPPORT_EMAIL,
  ].join('\n');
};

/**
 * Build the mailto: URL. encodeURIComponent leaves the line breaks intact once
 * the mail client decodes %0A, which is what keeps the numbered steps readable.
 */
export const buildRetrieveAccountMailto = (user: User, now: Date = new Date()): string => {
  const subject = encodeURIComponent(buildRetrieveAccountSubject());
  const body = encodeURIComponent(buildRetrieveAccountBody(user, now));
  return `mailto:${encodeURIComponent(user.email)}?subject=${subject}&body=${body}`;
};

export const openRetrieveAccountEmail = (user: User): void => {
  window.location.href = buildRetrieveAccountMailto(user);
};
